import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n, { SUPPORTED_LANG_CODES } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem('savenes_token');
}

function setStoredToken(token) {
  if (token) localStorage.setItem('savenes_token', token);
  else localStorage.removeItem('savenes_token');
  try { window.dispatchEvent(new CustomEvent('savenes:auth-changed')); } catch (_e) {}
}

function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: authHeaders()
      });
      setUser(response.data);
      // If the user's stored language preference differs from the current
      // i18next language, switch — but only if they haven't manually overridden
      // it in this browser (localStorage takes precedence so explicit choices
      // stick across re-logins).
      const stored = localStorage.getItem('savenes_language');
      const remote = response.data?.preferred_language;
      if (!stored && remote && SUPPORTED_LANG_CODES.includes(remote) && i18n.language !== remote) {
        i18n.changeLanguage(remote);
      }
    } catch (error) {
      setStoredToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // HIPAA: Idle session timeout — auto-logout after 15 min of inactivity
  useEffect(() => {
    if (!user) return;
    let idleTimer;
    const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        setStoredToken(null);
        setUser(false);
        window.location.href = '/login?reason=idle';
      }, IDLE_TIMEOUT);
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(evt => window.addEventListener(evt, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(idleTimer);
      events.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user]);

  // Retry helper for transient network / 5xx errors during auth flows.
  // Backend hot-reloads / preview env cold-starts briefly return no response.
  // Retries up to 3 times with exponential backoff (1s + 2s + 4s = 7s window).
  const authPost = async (url, body, opts = {}) => {
    let lastErr;
    for (let attempt = 0; attempt <= 3; attempt++) {
      try {
        return await axios.post(url, body, opts);
      } catch (err) {
        lastErr = err;
        const transient =
          !err.response ||
          err.code === 'ERR_NETWORK' ||
          err.code === 'ECONNABORTED' ||
          [502, 503, 504].includes(err.response?.status);
        if (!transient || attempt === 3) throw err;
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt))); // 1s, 2s, 4s
      }
    }
    throw lastErr;
  };

  const login = async (email, password) => {
    // Include trusted_device token from localStorage (if remembered earlier) via header.
    // We use a header instead of cookies because the platform ingress can't forward
    // credentials-mode CORS (rewrites ACAO to `*`), which iOS Safari then rejects.
    const trustedDevice = localStorage.getItem('savenes_trusted_device') || '';
    const headers = trustedDevice ? { 'X-Trusted-Device': trustedDevice } : {};
    const response = await authPost(
      `${API_URL}/api/auth/login`,
      { email, password },
      { headers }
    );
    // MFA challenge — caller must complete via completeMfaLogin
    if (response.data.requires_mfa) {
      return response.data;
    }
    const token = response.data.access_token;
    setStoredToken(token);
    setUser(response.data.user);
    return response.data;
  };

  const completeMfaLogin = async ({ mfa_token, code, method, remember_device }) => {
    const response = await authPost(
      `${API_URL}/api/auth/mfa/login-verify`,
      { mfa_token, code, method, remember_device: !!remember_device }
    );
    const token = response.data.access_token;
    setStoredToken(token);
    setUser(response.data.user);
    // Persist trusted_device token in localStorage (header-based; no cookie dependency)
    if (response.data.trusted_device_token) {
      localStorage.setItem('savenes_trusted_device', response.data.trusted_device_token);
    }
    return response.data;
  };

  const requestMfaEmailCode = async (mfa_token) => {
    const response = await authPost(
      `${API_URL}/api/auth/mfa/request-email-code`,
      { mfa_token, code: '' }
    );
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      userData
    );
    const token = response.data.access_token;
    setStoredToken(token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: authHeaders()
      });
    } catch (error) {}
    setStoredToken(null);
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
        headers: authHeaders()
      });
      if (response.data.access_token) {
        setStoredToken(response.data.access_token);
      }
      await checkAuth();
    } catch (error) {
      setStoredToken(null);
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfaLogin, requestMfaEmailCode, register, logout, refreshToken, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { getStoredToken, authHeaders };
export default AuthContext;
